import { ErrorBanner } from '@core/components';
import type { DomainError } from '@core/domain';
import { ErrorCategory } from '@core/domain/errors';
import { Text, Card, Section, LoadingSpinner, Heading, Dialog } from '@soteria/design-system-ve';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGenerateAuditReport } from '../../hooks/useGenerateAuditReport';
import { useGetAuditReport } from '../../hooks/useGetAuditReport';

import * as styles from './GenerateReportDialog.css';

interface GenerateReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
  sessionId: string;
}

/**
 * Generate Report Dialog
 *
 * Dialog shown when no audit reports are found, allowing users to generate a new report
 * or navigate back. Follows the established dialog patterns from CompleteAuditDialog.
 */
export function GenerateReportDialog({ isOpen, onClose, communityId, sessionId }: GenerateReportDialogProps) {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [errors, setErrors] = useState<DomainError[]>([]);

  const { generateAuditReport } = useGenerateAuditReport();
  const { refetch } = useGetAuditReport(communityId, sessionId);

  const handleBack = () => {
    navigate(-1);
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    setErrors([]);

    try {
      const result = await generateAuditReport(sessionId);

      if (result.success) {
        // Refetch the audit report data to show the newly generated report
        await refetch();
        onClose();
      } else {
        setErrors(result.errors || []);
      }
    } catch {
      setErrors([
        {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while generating the report',
          userMessage: 'An unexpected error occurred while generating the report',
          category: ErrorCategory.INTERNAL,
          correlationId: `client-error-${Date.now()}`,
        },
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog
      cancelAction={{
        label: 'Back',
        disabled: isGenerating,
      }}
      description="Review the session details before generating a comprehensive audit report."
      isOpen={isOpen}
      primaryAction={{
        label: isGenerating ? <LoadingSpinner /> : 'Generate Report',
        onClick: handleGenerateReport,
        disabled: isGenerating,
        variant: 'primary',
      }}
      size="md"
      title="No Reports Found"
      onClose={handleBack}
    >
      <div className={styles.dialogContent}>
        <div className={styles.contentSection}>
          <Heading level="h3">Report Generation</Heading>
        </div>

        <div className={styles.contentSection}>
          <Card>
            <Section spacing="medium">
              <Text className={styles.descriptionText}>
                Generate a comprehensive audit report for this completed session. The report will include performance metrics, label analysis, and detailed
                comparisons between expected and actual results.
              </Text>
            </Section>
          </Card>
        </div>

        {errors.length > 0 && (
          <div className={styles.contentSection}>
            <ErrorBanner errors={errors} title="There was an error generating the report:" />
          </div>
        )}
      </div>
    </Dialog>
  );
}
